import React, { createContext, useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { cachedRequest } from '../utils/requestCache';
import { logGuestSession } from '../utils/logEvent';
import Logo from '../assets/images/BloomPal_MainLogo.png';

export const UserContext = createContext();

// Hook to use UserContext
export const useUserContext = () => {
  return useContext(UserContext);
};

export const UserProvider = ({ children }) => {
  // Auth token
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('authToken') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(!!authToken);
  const navigate = useNavigate();
  const { search } = useLocation();


  // Session ID (always generate if missing)
  // Note: setSessionId is used internally for state management but not exported
  // eslint-disable-next-line no-unused-vars
  const [sessionId, setSessionId] = useState(() => {
    let sid = localStorage.getItem('sessionId');
    if (!sid) {
      sid = `session-${Date.now()}`;
      localStorage.setItem('sessionId', sid);
    }
    return sid;
  });

  // Visitor ID: only use what's already in localStorage; defer generation if empty
  const [visitorId, setVisitorId] = useState(() => localStorage.getItem('visitorId') || '');

  // Guest name & reason
  const [name, _setName] = useState(() => localStorage.getItem('guestName') || '');
  const [reason, _setReason] = useState(() => localStorage.getItem('userReason') || '');
  
  // User object (for logged-in users)
  const [user, setUser] = useState(null);

  // If no visitorId on mount, generate one now (but respect any that WelcomePage may have set)
  useEffect(() => {
    if (!visitorId) {
      const newVid = 'guest-' + Math.random().toString(36).slice(2);
      localStorage.setItem('visitorId', newVid);
      setVisitorId(newVid);
    }
  }, [visitorId]);
  // Persist authToken to localStorage and axios headers
  useEffect(() => {
    if (authToken) {
      localStorage.setItem('authToken', authToken);
      api.defaults.headers.common.Authorization = `Token ${authToken}`;
    } else {
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common.Authorization;
    }
  }, [authToken]);

  // Fetch authenticated user profile (with caching to prevent duplicate calls)
  useEffect(() => {
    if (!authToken) {
      setUser(null);
      return;
    }
    cachedRequest(api.get, '/api/auth/user/')
      .then(({ data }) => {
        console.log('[UserContext] Fetched user data from API:', data);
        console.log('[UserContext] User ID from database:', data.id, '(type:', typeof data.id, ')');
        
        if (data.name) setName(data.name);
        if (data.reason) setReason(data.reason);
        // Store the full user object (including id from database)
        setUser(data);
        
        console.log('[UserContext] User object stored in context:', data);
      })
      .catch((error) => {
        console.error('[UserContext] Error fetching user:', error);
        setAuthToken('');
        setUser(null);
      });
  }, [authToken]);
  
  // Helpers to sync name & reason to localStorage
  const setName = (n) => {
    _setName(n);
    localStorage.setItem('guestName', n);
  };
  const setReason = (r) => {
    _setReason(r);
    localStorage.setItem('userReason', r);
  };

    // 1) your existing login effect…
    useEffect(() => {
      setIsLoggedIn(!!authToken);
    }, [authToken]);
    // 2) Post–OAuth redirect handler
  useEffect(() => {
    if (!isLoggedIn) return;

    const params = new URLSearchParams(search);
    const justSynced = params.get('calendar_synced') === 'true';
    const sid       = params.get('session_id');

    if (justSynced && sid) {
      api.post(
        '/api/rest/v1/calendar/transfer/',
        { sessionId: sid },
        { headers: { Authorization: `Token ${authToken}` } }
      )
      .then(() => console.log('➜ transferred calendar to user'))
      .catch(console.error)
      .finally(() => {
        params.delete('calendar_synced');
        params.delete('session_id');
        navigate(
          { search: params.toString() },
          { replace: true }
        );      });
    }
  }, [isLoggedIn, search, authToken, navigate]);




  // Record guest session exactly once when all pieces are in place
  useEffect(() => {
    if (!authToken && visitorId && sessionId && name && reason) {
      logGuestSession(visitorId, sessionId, name, reason);
    }
  }, [visitorId, sessionId, name, reason, authToken]);



  // Logout resets tokens and session
  const logout = () => {
    setAuthToken('');
    setUser(null);
//    setName('');
//    setReason('');
//    localStorage.removeItem('visitorId');
//    const newSid = `session-${Date.now()}`;
//    localStorage.setItem('sessionId', newSid);
//    setSessionId(newSid);
  };

  return (
    <UserContext.Provider value={{
      authToken, setAuthToken,
      name, setName,
      userName: name,
      reason, setReason,
      isLoggedIn,
      sessionId,
      visitorId,
      setVisitorId,
      logout,
      brandLogo: Logo,
      user, // Include user object with id
    }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;